import { html, nothing } from 'lit';
import { connect } from 'pwa-helpers/connect-mixin';
import { nanoid } from 'nanoid';
import LitElementExtended from '../classes/LitElementExtended';
import './tef-coms-group';

import store, { RESET } from '../store';
import { init, occlThemeLoad, occlThemeError } from '../store/config';
import { validate } from '../store/auth';
import { load, save } from '../store/consents';

import config from '../config';
import service from '../service';
import tefGtmTracker from '../service/tracker';
import TemplateResolver from './templates/templateResolver';

/* eslint-disable no-underscore-dangle */

/**
 * `tef-coms`
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
export class TefComs extends connect(store)(LitElementExtended) {
  constructor() {
    super();
    store.dispatch({ type: RESET });
    // init props
    this.accessToken = undefined;
    this.customerToken = undefined;
    this.idtoken = undefined;
    this.registerToken = undefined;
    this.newcustomer = false;
    this.lifecycle = 'webAppRegistrationProcess';
    this.apiUri = 'http://localhost:3000';
    this.apiUri4p = undefined;
    this.accesstoken4p = undefined;
    this.mode = '';
    this.occluri = undefined;
    this.stylecontext = '';
    this.channel = 'Web';
    this.vonr = '';
    this.traceid = nanoid();
    this.references = undefined;
    this.comment = undefined;
    this.spId = undefined;
    this.lcId = undefined;
    this.registerId = undefined;
    this.usertype = undefined;
    this.consentGroups = undefined;
    this.details = undefined;
    this.consentGroupsView = undefined;
    this.consentGroupsOrigin = undefined;
    this.editmodeActiveGroupInd = -1;
    this.globalOfferId = undefined;
    this.initializing = true;
    this.fatal = undefined;

    service.bindTefComs(this);
    service.tracker = tefGtmTracker;
  }

  static get properties() {
    return {
      accessToken: { type: String },
      customerToken: { type: String },
      idtoken: { type: String },
      newcustomer: { type: Boolean },
      registerToken: { type: String },
      lifecycle: { type: String },
      apiUri: { type: String },
      apiUri4p: { type: String },
      accesstoken4p: { type: String },
      mode: { type: String },
      editmodeActiveGroupInd: { type: Number },
      occluri: { type: String },
      stylecontext: { type: String },
      channel: { type: String },
      vonr: { type: String },
      traceid: { type: String },
      references: { type: String },
      usertype: { type: String },
      comment: { type: String },
      spId: { type: String },
      lcId: { type: String },
      registerId: { type: String },
      consentGroups: { type: Array },
      consentGroupsView: { type: Array },
      consentGroupsOrigin: { type: Array },
      globalOfferId: { type: String },
      initializing: { type: Boolean },
      fatal: { type: Object },
    };
  }

  async connectedCallback() {
    try {
      if (localStorage.getItem('tef-coms-debug-mode')) {
        let timestampInit = null;
        let timestampReady = null;

        this.addEventListener('tef-coms', (e) => {
          const { detail } = e;
          const channel = detail.level === 'error' ? 'error' : 'info';
          console[channel]('tef-coms: event', detail.name, e);
          if (detail.name === 'config/INIT') {
            timestampInit = performance && performance.now ? performance.now() : 0;
          }
          if (detail.name === 'load/READY') {
            timestampReady = performance && performance.now ? performance.now() : 0;
            const el = customElements.get('tef-coms');
            const infoVersion = `version ${el.version}, build ${el.build.target}`;
            const infoReady = `Micro-frontend ready in ${Math.trunc(
              timestampReady - timestampInit,
            )} ms, `;
            console.log(infoReady + infoVersion);
          }
        });
      }
      store.dispatch({ type: 'CONNECTED' }); // used for performance measurements !!
      super.connectedCallback(); // start rendering immediately !!
      await this.load();
    } catch (error) {
      // ignore -- has error condition instead
    }
  }

  async load() {
    const {
      accessToken,
      customerToken,
      idtoken,
      newcustomer,
      registerToken,
      lifecycle,
      apiUri,
      apiUri4p,
      accesstoken4p,
      mode,
      occluri,
      stylecontext,
      channel,
      vonr,
      traceid,
      references,
      comment,
      usertype,
      spId,
      lcId,
      registerId,
    } = this;

    await store.dispatch(
      init({
        universalAccessToken: accessToken,
        customerToken,
        idtoken,
        newcustomer,
        registerToken,
        lifecycle,
        apiUri,
        apiUri4p,
        accesstoken4p,
        mode,
        occluri,
        stylecontext,
        channel,
        vonr,
        traceid,
        references,
        comment,
        usertype,
        spId,
        lcId,
        registerId,
      }),
    );
    await store.dispatch(validate());
    await store.dispatch(load());
  }

  // eslint-disable-next-line class-methods-use-this
  async save() {
    await store.dispatch(save());
  }

  stateChanged(state) {
    // state changes from Redux
    // destruct state
    const {
      config: { occlReady, error: configError },
      auth: { processing: authProcessing, error: authError },
      consents: {
        processing: consentsProcessing,
        error: consentsError,
        consentGroups,
        consentGroupsView,
        consentGroupsOrigin,
        editmodeActiveGroupInd,
        details,
      },
      globalOfferId,
    } = state;
    // map state to props
    this.consentGroups = consentGroups;
    this.details = details;
    this.consentGroupsView = consentGroupsView;
    this.consentGroupsOrigin = consentGroupsOrigin;
    this.editmodeActiveGroupInd = editmodeActiveGroupInd;
    this.globalOfferId = globalOfferId;
    this.initializing = !occlReady || authProcessing || (consentsProcessing && !consentGroups);
    this.fatal = configError || authError || (consentGroups ? undefined : consentsError);
  }

  get viewTemplate() {
    const {
      consents: { editmodeActiveGroupInd },
    } = store.getState();
    if (editmodeActiveGroupInd === -1) {
      return this.template;
    }

    const changesMade = JSON.stringify(this.consentGroups[editmodeActiveGroupInd].consents)
        !== JSON.stringify(
          this.consentGroupsView[editmodeActiveGroupInd].consents,
        )
      || !!this.consentGroupsView[editmodeActiveGroupInd].consents.find(
        (c) => c.modeconstraint === 'edit',
      );

    const consentGroup = this.consentGroups[editmodeActiveGroupInd];
    const viewTemplate = TemplateResolver.getInstance(!!this.occluri).resolve(
      'viewTemplate',
    );

    return viewTemplate(consentGroup, editmodeActiveGroupInd, changesMade);
  }

  get pageFooter() {
    const pageFooter = TemplateResolver.getInstance(!!this.occluri).resolve(
      'pageFooter',
    );

    return this.consentGroups.length > 0
      && this.details
      && this.details.pageFooter
      ? pageFooter(this)
      : nothing;
  }

  get template() {
    const changesMadePage = this.consentGroups.reduce(
      (acc, currentValue, currentIndex) => acc
        || JSON.stringify(currentValue.consents)
          !== JSON.stringify(this.consentGroupsOrigin[currentIndex].consents)
        || !!currentValue.consents.find((c) => c.modeconstraint === 'edit'),
      false,
    );
    let buttonMode;
    let buttonModeButton;
    if (
      this.consentGroups.length > 0
      && this.mode
      && this.mode.includes('buttons')
    ) {
      [buttonMode, buttonModeButton] = this.mode.split(':');
    }

    const resolver = TemplateResolver.getInstance(!!this.occluri);
    const consentGroupTemplate = resolver.resolve('consentGroupTemplate');
    const buttonModeTemplate = resolver.resolve('buttonModeTemplate');

    return html` ${this.consentGroups.map((consentGroup, idx) => consentGroupTemplate(consentGroup, idx, this))}
    ${this.pageFooter}
    ${buttonMode
    ? buttonModeTemplate(buttonModeButton, changesMadePage)
    : nothing}`;
  }

  updated() {
    this.replaceElements(this.occluri);
  }

  render() {
    if (this.fatal) {
      return html`<!-- fatal error condition -->`;
    }

    const {
      occl: { theme },
    } = config;
    const {
      config: { stylecontext = '', occluri = '' },
    } = store.getState();

    if (occluri) {
      const themeUri = theme(occluri);
      const classes = [
        ...stylecontext
          .split(' ')
          .filter((s) => s)
          .map((s) => `context-${s}`),
        'tef-coms',
        'section',
        'section--size-flat',
      ];

      const occlLoad = () => store.dispatch(occlThemeLoad());
      const occlError = () => store.dispatch(occlThemeError());

      return html`
        <link
          rel="stylesheet"
          href="${themeUri}"
          @load="${occlLoad}"
          @error="${occlError}"
        />
        ${this.initializing
    ? nothing
    : html`
              <div class=${classes.join(' ')}>${this.dispatchTemplate()}</div>
            `}
      `;
    }

    return html`<div>${this.dispatchTemplate()}</div>`;
  }

  dispatchTemplate() {
    if (!this.consentGroups) {
      return nothing;
    }
    return html`${this.mode && this.mode.includes('multiedit')
      ? this.viewTemplate
      : this.template}`;
  }
}

export default TefComs;
window.customElements.define('tef-coms', TefComs);
